import React, { useState, useMemo, useEffect } from 'react';
// @ts-ignore
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { POT_COLOURS } from '../constants/potColours';
import {
  fvLump,
  fvContributions,
  // mortgageBalanceAfter, calcMortgagePayment, drawdownIncome — available for future use
  mortgagePayoffMonths,
} from '../utils/planCalculations';

interface YearDataPoint {
  year: number;
  age: number;
  propertyEquity: number;
  pension: number;
  isa: number;
  cash: number;
  netWorth: number;
  mortgageBalance: number;
  doNothingNetWorth: number;
}

interface TransitionEvent {
  label: string;
  year: number;
  age: number;
  description: string;
}

const PlanPhasedPlan: React.FC<{ data: ClientData; onUpdate: (updates: Partial<ClientData>) => void }> = ({
  data,
  onUpdate,
}) => {
  const [showDetail, setShowDetail] = useState(true);

  // On mount, mark plan as built and calculate monthly committed
  useEffect(() => {
    const totalMonthlyCommitted =
      (data.planPensionFromDividendReduction || 0) +
      (data.planIsaMonthlyContribution || 0) +
      (data.planMortgageOverpayment || 0) +
      (data.planMortgageAnnualLumpSum ? data.planMortgageAnnualLumpSum / 12 : 0);

    onUpdate({
      planBuilt: true,
      planFinalMonthlyCommitted: totalMonthlyCommitted,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cascade-aware contribution helpers ──────────────────────────────────
  // These account for redirected cashflow when EF completes or mortgage pays off.
  const termMonths = data.mortgageTermYears * 12;
  const payoffMonthsCalc = mortgagePayoffMonths(
    data.mortgageBalance,
    data.mortgageRate,
    termMonths,
    data.planMortgageOverpayment || 0,
    data.planMortgageAnnualLumpSum || 0,
  );
  const currentYear = new Date().getFullYear();

  // Year when EF is complete (calendar year)
  const efCompleteYear = data.planEfCompleteDate
    ? new Date(data.planEfCompleteDate).getFullYear()
    : 0;

  // Year when mortgage is paid off (calendar year)
  const mortgagePayoffYear = payoffMonthsCalc > 0
    ? currentYear + Math.floor(payoffMonthsCalc / 12)
    : 0;

  // Monthly mortgage payment (standard + overpayment) freed at payoff
  const mortgageFreedMonthly =
    (data.mortgagePayment || 0) + (data.planMortgageOverpayment || 0);

  function pensionContribInYear(calendarYear: number): number {
    const base =
      (data.pensionCompanyFunded || 0) +
      (data.planPensionFromDividendReduction || 0);
    let extra = 0;

    // EF redirect → pension
    if (data.planEfRedirectTo === 'pension' && efCompleteYear && calendarYear >= efCompleteYear) {
      extra += data.planEfMonthlyTopUp || 0;
    }

    // Mortgage freed → pension
    if (data.planMortgageRedirectTo === 'pension' && mortgagePayoffYear && calendarYear >= mortgagePayoffYear) {
      extra += mortgageFreedMonthly;
    }

    return base + extra;
  }

  function isaContribInYear(calendarYear: number): number {
    let base = data.planIsaMonthlyContribution || 0;

    // EF redirect → ISA
    if (data.planEfRedirectTo === 'isa' && efCompleteYear && calendarYear >= efCompleteYear) {
      base += data.planEfMonthlyTopUp || 0;
    }

    // Mortgage freed → ISA
    if ((data.planMortgageRedirectTo?.toLowerCase() === 'isa') && mortgagePayoffYear && calendarYear >= mortgagePayoffYear) {
      base += mortgageFreedMonthly;
    }

    return base;
  }

  // Calculate year-by-year data (cascade-aware)
  const chartData = useMemo(() => {
    const yearsToFreedom = data.targetFreedomAge - data.clientAge;
    const result: YearDataPoint[] = [];

    // Running totals for incremental FV calculation with variable contributions
    let pensionRunning = data.pensionBalance || 0;
    let isaRunning = data.investmentsBalance || 0;

    // Do-nothing running totals (only existing commitments, no plan decisions)
    let dnPensionRunning = data.pensionBalance || 0;
    let dnIsaRunning = data.investmentsBalance || 0;

    const pensionGrowthRate = data.assumptionPensionGrowth || 0.08;
    const isaGrowthRate = data.assumptionIsaGrowth || 0.08;

    // Do-nothing contributions: only existing pension, no ISA contrib, no plan decisions
    const dnPensionMonthly = data.pensionCompanyFunded || 0; // no dividend reduction
    const dnIsaMonthly = 0; // no new ISA (or use existing if any)

    // Do-nothing mortgage: standard payments only, no overpayment
    const dnPayoffMonths = mortgagePayoffMonths(
      data.mortgageBalance, data.mortgageRate, termMonths, 0, 0,
    );

    for (let y = 0; y <= yearsToFreedom; y++) {
      const year = currentYear + y;
      const age = data.clientAge + y;

      // Property equity (same growth for both scenarios)
      const propertyValue =
        data.propertyValue * Math.pow(1 + data.assumptionPropertyGrowth, y);

      // Plan mortgage balance
      let mortBal = 0;
      if (y * 12 < payoffMonthsCalc) {
        mortBal = data.mortgageBalance * Math.max(0, 1 - (y * 12) / payoffMonthsCalc);
      }
      const propertyEquity = propertyValue - Math.max(mortBal, 0);

      // Do-nothing mortgage balance
      let dnMortBal = 0;
      if (y * 12 < dnPayoffMonths) {
        dnMortBal = data.mortgageBalance * Math.max(0, 1 - (y * 12) / dnPayoffMonths);
      }
      const dnPropertyEquity = propertyValue - Math.max(dnMortBal, 0);

      // Cash (same for both — plan doesn't directly alter cash)
      const totalSavings =
        (data.savingsAccounts || []).reduce((s: number, a: any) => s + (a.balance || 0), 0) +
        (data.efAccount1Balance || 0) +
        (data.efAccount2Balance || 0);
      const cashGrowth = data.assumptionCashRate || 0.02;
      const cash = totalSavings * Math.pow(1 + cashGrowth, y);

      // Record values
      const pension = pensionRunning;
      const isa = isaRunning;
      const netWorth = propertyEquity + pension + isa + cash;

      const dnNetWorth = dnPropertyEquity + dnPensionRunning + dnIsaRunning + cash;

      result.push({
        year,
        age,
        propertyEquity: Math.max(0, propertyEquity),
        pension: Math.max(0, pension),
        isa: Math.max(0, isa),
        cash: Math.max(0, cash),
        netWorth: Math.max(0, netWorth),
        mortgageBalance: Math.max(0, mortBal),
        doNothingNetWorth: Math.max(0, dnNetWorth),
      });

      // Grow plan for next year
      const pensionContrib = pensionContribInYear(year);
      const isaContrib = isaContribInYear(year);
      pensionRunning = pensionRunning * (1 + pensionGrowthRate) + pensionContrib * 12;
      isaRunning = isaRunning * (1 + isaGrowthRate) + isaContrib * 12;

      // Grow do-nothing for next year
      dnPensionRunning = dnPensionRunning * (1 + pensionGrowthRate) + dnPensionMonthly * 12;
      dnIsaRunning = dnIsaRunning * (1 + isaGrowthRate) + dnIsaMonthly * 12;
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Calculate transition events
  const transitionEvents = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const events: TransitionEvent[] = [];

    // TODAY
    events.push({
      label: 'TODAY',
      year: currentYear,
      age: data.clientAge,
      description: `Age ${data.clientAge}`,
    });

    // EF COMPLETE (if applicable)
    if ((data.planEfMonthlyTopUp || 0) > 0 && efCompleteYear > 0) {
      const efAge = data.clientAge + (efCompleteYear - currentYear);
      const redirectLabel = data.planEfRedirectTo === 'pension' ? 'Pension'
        : data.planEfRedirectTo?.toLowerCase() === 'isa' ? 'ISA'
        : data.planEfRedirectTo || 'ISA';
      events.push({
        label: '✓ Emergency fund complete',
        year: efCompleteYear,
        age: efAge,
        description: `+${formatCurrency(data.planEfMonthlyTopUp || 0)}/mo → ${redirectLabel}`,
      });
    }

    // MORTGAGE-FREE (if applicable)
    if (data.mortgageBalance && data.mortgageBalance > 0 && mortgagePayoffYear > 0) {
      const payoffAge = data.clientAge + Math.floor(payoffMonthsCalc / 12);
      const redirectLabel = data.planMortgageRedirectTo === 'pension' ? 'Pension'
        : data.planMortgageRedirectTo?.toLowerCase() === 'isa' ? 'ISA'
        : data.planMortgageRedirectTo || 'ISA';
      events.push({
        label: '✓ Mortgage-free',
        year: mortgagePayoffYear,
        age: payoffAge,
        description: `+${formatCurrency(mortgageFreedMonthly)}/mo → ${redirectLabel}`,
      });
    }

    // FREEDOM AGE
    const yearsToFreedom = data.targetFreedomAge - data.clientAge;
    const freedomYear = currentYear + yearsToFreedom;
    events.push({
      label: 'FREEDOM AGE',
      year: freedomYear,
      age: data.targetFreedomAge,
      description: `${yearsToFreedom} years to financial freedom`,
    });

    return events.sort((a, b) => a.year - b.year);
  }, [data]);

  // Calculate summary callouts
  const summaryData = useMemo(() => {
    const lastYear = chartData[chartData.length - 1];
    if (!lastYear) {
      return {
        netWorthAtFreedom: 0,
        monthlyIncomeAtFreedom: 0,
        pensionAtFreedom: 0,
        isaAtFreedom: 0,
        doNothingAtFreedom: 0,
        delta: 0,
      };
    }

    // Monthly income = 4% rule / 12
    const monthlyIncome = (lastYear.pension + lastYear.isa) * 0.04 / 12;

    return {
      netWorthAtFreedom: lastYear.netWorth,
      monthlyIncomeAtFreedom: monthlyIncome,
      pensionAtFreedom: lastYear.pension,
      isaAtFreedom: lastYear.isa,
      doNothingAtFreedom: lastYear.doNothingNetWorth,
      delta: lastYear.netWorth - lastYear.doNothingNetWorth,
    };
  }, [chartData]);

  // Generate plan narrative
  const planNarrative = useMemo(() => {
    if (chartData.length < 2) return '';

    const yearsToFreedom = data.targetFreedomAge - data.clientAge;
    const lastYear = chartData[chartData.length - 1];
    const mortgagePayoffYears = data.mortgageBalance
      ? (mortgagePayoffMonths(
          data.mortgageBalance,
          data.mortgageRate,
          data.mortgageTermYears * 12,
          data.planMortgageOverpayment || 0,
          data.planMortgageAnnualLumpSum || 0
        ) / 12)
      : null;

    let narrative = `By age ${data.targetFreedomAge}, you'll have built a net worth of ${formatCurrency(lastYear.netWorth)}. `;

    if (mortgagePayoffYears !== null && mortgagePayoffYears < yearsToFreedom) {
      narrative += `Your mortgage will be paid off in ${Math.round(mortgagePayoffYears)} years. `;
    }

    narrative += `This comprises ${formatCurrency(lastYear.pension)} in your pension, ${formatCurrency(lastYear.isa)} in your ISA, and ${formatCurrency(lastYear.propertyEquity)} in property equity. `;
    narrative += `At this point, you'll have a monthly income of ${formatCurrency(summaryData.monthlyIncomeAtFreedom)} from your investments alone, giving you true financial freedom.`;

    return narrative;
  }, [chartData, data, summaryData]);

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data: any = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-semibold text-gray-900">{data.year} (Age {data.age})</p>
          <p style={{ color: POT_COLOURS.property }} className="text-sm">Property equity: {formatCurrency(data.propertyEquity)}</p>
          <p style={{ color: POT_COLOURS.pension }} className="text-sm">Pension pot: {formatCurrency(data.pension)}</p>
          <p style={{ color: POT_COLOURS.investments }} className="text-sm">ISA & investments: {formatCurrency(data.isa)}</p>
          <p style={{ color: POT_COLOURS.cash }} className="text-sm">Cash savings: {formatCurrency(data.cash)}</p>
          <p className="font-semibold text-gray-900 mt-1">
            Net Worth: {formatCurrency(data.netWorth)}
          </p>
          {data.doNothingNetWorth > 0 && (
            <p className="text-gray-400 text-xs mt-1">
              Without plan: {formatCurrency(data.doNothingNetWorth)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Format YAxis as £K
  const formatYAxis = (value: number) => `£${(value / 1000).toFixed(0)}K`;

  return (
    <div className="w-full bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="px-8 py-12 border-b border-gray-200">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Where Your Money Goes</h1>
        <p className="text-lg text-gray-600">Every pound committed today, allocated by wrapper — age by age.</p>
      </div>

      <div className="px-8 py-12">
        <p className="text-sm text-slate-400 mb-2">
          Your committed contributions, allocated by wrapper and compounding to age {data.targetFreedomAge || 55}.
        </p>

        {/* Chart Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-12">
          {(() => {
            // Brief 49 S1 — diagnostic console.log
            // eslint-disable-next-line no-console
            console.log('[Brief49] PlanPhasedPlan chartData length:', chartData.length, 'First 3:', JSON.stringify(chartData.slice(0, 3)));
            return null;
          })()}
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="age"
                tick={{ fill: '#6b7280', fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatYAxis}
                tick={{ fill: '#6b7280', fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="square"
              />

              {/* Brief 47 S5 — Property equity removed from wrapper allocation chart */}
              <Bar dataKey="cash" stackId="wealth" fill={POT_COLOURS.cash} name="Cash savings" />
              <Bar dataKey="isa" stackId="wealth" fill={POT_COLOURS.investments} name="ISA & investments" />
              <Bar dataKey="pension" stackId="wealth" fill={POT_COLOURS.pension} name="Pension pot" />

              {/* Brief 27 — EF complete inflection point (savings redirect) */}
              {efCompleteYear > 0 && efCompleteYear > currentYear && efCompleteYear < currentYear + (data.targetFreedomAge - data.clientAge) && (
                <ReferenceLine
                  x={`${efCompleteYear}`}
                  stroke="#0d9488"
                  strokeDasharray="4 4"
                  label={{ value: "Savings redirect", position: "top", fill: "#0d9488", fontSize: 12 }}
                />
              )}

              {/* Brief 26 Section 8a — Mortgage clears inflection point */}
              {mortgagePayoffYear > 0 && mortgagePayoffYear < currentYear + (data.targetFreedomAge - data.clientAge) && (
                <ReferenceLine
                  x={`${mortgagePayoffYear}`}
                  stroke="#64DAD2"
                  strokeDasharray="4 4"
                  label={{
                    value: `Mortgage clears → £${(mortgageFreedMonthly).toLocaleString()}/mo redirected`,
                    position: 'top',
                    fontSize: 11,
                    fill: '#64DAD2',
                  }}
                />
              )}

              {/* Reference line at freedom age */}
              <ReferenceLine
                x={`${new Date().getFullYear() + (data.targetFreedomAge - data.clientAge)}`}
                stroke="#ef4444"
                strokeDasharray="5 5"
                label={{
                  value: 'Freedom Age',
                  position: 'top',
                  fill: '#ef4444',
                  fontSize: 12,
                }}
              />

              {/* Freedom Number + trajectory lines removed — Brief 46C S1 */}
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-xs text-slate-400 text-center mt-2">
            Assumes 8% annual growth for pension and investments, 1% for cash. Planning purposes only.
          </p>
        </div>

        {/* Brief 46C S1d — Annual commitment summary */}
        {(() => {
          const pensionAnnual = (data.pensionCompanyFunded || 0) * 12;
          const isaAnnual = (data.planIsaMonthlyContribution || data.isaContribution || 0) * 12;
          const savingsAnnual = (data.efTopUp || 0) * 12;
          const mortgageOPAnnual = (data.planMortgageOverpayment || 0) * 12;
          const totalAnnual = pensionAnnual + isaAnnual + savingsAnnual + mortgageOPAnnual;
          const gross = ((data.salary || 0) + (data.dividends || 0));
          const pctOf = (v: number) => gross > 0 ? `${((v / gross) * 100).toFixed(1)}%` : '—';
          return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-12">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">Your Annual Commitment</h3>
              <div className="space-y-2 text-sm">
                {pensionAnnual > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pension (employer)</span>
                    <span className="font-medium">{formatCurrency(pensionAnnual)}/yr <span className="text-gray-400 ml-2">{pctOf(pensionAnnual)} of gross</span></span>
                  </div>
                )}
                {isaAnnual > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">ISA</span>
                    <span className="font-medium">{formatCurrency(isaAnnual)}/yr <span className="text-gray-400 ml-2">{pctOf(isaAnnual)} of gross</span></span>
                  </div>
                )}
                {savingsAnnual > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Savings</span>
                    <span className="font-medium">{formatCurrency(savingsAnnual)}/yr <span className="text-gray-400 ml-2">{pctOf(savingsAnnual)} of gross</span></span>
                  </div>
                )}
                {mortgageOPAnnual > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mortgage overpayment</span>
                    <span className="font-medium">{formatCurrency(mortgageOPAnnual)}/yr <span className="text-gray-400 ml-2">{pctOf(mortgageOPAnnual)} of gross</span></span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold">
                  <span className="text-gray-700">Total committed</span>
                  <span className="text-ma-navy">{formatCurrency(totalAnnual)}/yr <span className="text-gray-400 ml-2 font-medium">{pctOf(totalAnnual)} of gross</span></span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Your employer pension contribution costs you nothing from take-home — the company pays it directly and claims CT relief. Every other row comes from your personal income.
              </p>
            </div>
          );
        })()}

        {/* Brief 12: Three key stats below chart */}
        <div className="grid grid-cols-3 gap-4 mb-12 text-center">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-500 font-medium mb-1">Net worth at freedom</p>
            <p className="text-xl font-bold text-ma-navy">
              {formatCurrency(summaryData.netWorthAtFreedom)}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-500 font-medium mb-1">Monthly income at freedom</p>
            <p className="text-xl font-bold text-ma-navy">
              {formatCurrency(summaryData.monthlyIncomeAtFreedom)}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-500 font-medium mb-1">Freedom age</p>
            <p className="text-xl font-bold text-ma-navy">
              {data.targetFreedomAge}*
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">*Confirmed in Voyant — see next step</p>
          </div>
        </div>

        {/* Transition Timeline */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Your journey to freedom</h2>
          <div className="flex items-center gap-4 overflow-x-auto pb-4">
            {transitionEvents.map((event, idx) => (
              <React.Fragment key={idx}>
                <div className="flex flex-col items-center min-w-max">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-sm mb-2">
                    {event.year}
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-gray-900 text-sm">{event.label}</p>
                    <p className="text-xs text-gray-500">Age {event.age}</p>
                    <p className="text-xs text-gray-600 mt-1">{event.description}</p>
                  </div>
                </div>
                {idx < transitionEvents.length - 1 && (
                  <div className="h-0.5 w-12 bg-gradient-to-r from-blue-400 to-purple-400 hidden md:block" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Freedom Age Insight */}
        {(() => {
          // Only show if Voyant S1 has been captured
          if (!data.voyantS1Captured) {
            return (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-12 text-center">
                <p className="text-sm text-gray-500 italic">
                  Complete Voyant Break 2 to see your freedom age insight.
                </p>
              </div>
            );
          }

          // Only show if mortgage pays off before Voyant retirement age
          const mortPayoffAge = data.planMortgagePayoffAge || 0;
          const voyantAge = data.voyantS1RetirementAge || 0;
          if (!mortPayoffAge || !voyantAge || mortPayoffAge >= voyantAge || data.mortgageBalance <= 0) {
            return null;
          }

          const freed = mortgageFreedMonthly;
          const annualFreed = freed * 12;
          const annualGapNeeded = data.voyantS1AnnualSavingsNeeded || 0;

          let estimateText = '';
          if (annualGapNeeded > 0) {
            const fraction = Math.min(1, annualFreed / annualGapNeeded);
            const rawYears = fraction * (voyantAge - data.targetFreedomAge);
            const capped = Math.min(rawYears, 5);
            const low = Math.max(0.5, Math.floor(capped * 2) / 2);
            const high = Math.min(5, Math.ceil(capped * 2) / 2 + 0.5);
            estimateText = `approximately ${low}–${high} years`;
          }

          const s2Monthly = annualGapNeeded > 0 ? Math.round(annualGapNeeded / 12) : 0;

          return (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-12">
              <h3 className="text-lg font-bold text-ma-navy mb-3">Your Freedom Age Insight</h3>
              <p className="text-sm text-gray-700 mb-3">
                Voyant projects your freedom age at <strong>{voyantAge}</strong> with today's decisions.
              </p>
              <p className="text-sm text-gray-700 mb-3">
                When your mortgage clears at age <strong>{Math.round(mortPayoffAge)}</strong>,{' '}
                <strong>{formatCurrency(freed)}/month</strong> is freed up.
                {data.planMortgageRedirectTo === 'pension' && (
                  <> If that redirects to your pension, your freedom age could move forward by{' '}
                  <strong className="text-green-700">{estimateText}</strong>.</>
                )}
              </p>
              {s2Monthly > 0 && (
                <p className="text-sm text-amber-800 font-medium mt-3">
                  That's the conversation for Session 2. Your business needs to generate{' '}
                  <strong>{formatCurrency(s2Monthly)}/month</strong>.
                </p>
              )}
            </div>
          );
        })()}

        {/* Summary Callouts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Net Worth at Freedom */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xl">📈</span>
              <h3 className="font-semibold text-gray-900">Net Worth at Freedom</h3>
            </div>
            <p className="text-3xl font-bold text-blue-900">
              {formatCurrency(summaryData.netWorthAtFreedom)}
            </p>
          </div>

          {/* Monthly Income */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xl">🐷</span>
              <h3 className="font-semibold text-gray-900">Monthly Income at Freedom</h3>
            </div>
            <p className="text-3xl font-bold text-green-900">
              {formatCurrency(summaryData.monthlyIncomeAtFreedom)}
            </p>
            <p className="text-xs text-green-700 mt-1">From 4% rule on combined assets</p>
          </div>

          {/* Pension at Freedom */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xl">🏛️</span>
              <h3 className="font-semibold text-gray-900">Pension at Freedom</h3>
            </div>
            <p className="text-3xl font-bold text-purple-900">
              {formatCurrency(summaryData.pensionAtFreedom)}
            </p>
          </div>

          {/* ISA at Freedom */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border border-orange-200">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xl">🏠</span>
              <h3 className="font-semibold text-gray-900">ISA at Freedom</h3>
            </div>
            <p className="text-3xl font-bold text-orange-900">
              {formatCurrency(summaryData.isaAtFreedom)}
            </p>
          </div>
        </div>

        {/* Do-nothing vs Plan comparison */}
        {summaryData.delta > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-12">
            <h3 className="font-bold text-gray-900 mb-4 text-lg">The value this session created</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Without today's decisions</p>
                <p className="text-xl font-bold text-gray-500">{formatCurrency(summaryData.doNothingAtFreedom)}</p>
                <p className="text-xs text-gray-400">at age {data.targetFreedomAge}</p>
              </div>
              <div>
                <p className="text-gray-600">With today's plan</p>
                <p className="text-xl font-bold text-ma-navy">{formatCurrency(summaryData.netWorthAtFreedom)}</p>
                <p className="text-xs text-gray-400">at age {data.targetFreedomAge}</p>
              </div>
              <div>
                <p className="text-gray-600">Difference</p>
                <p className="text-xl font-bold text-green-700">+{formatCurrency(summaryData.delta)}</p>
                <p className="text-xs text-green-600 font-medium">the value this session created</p>
              </div>
            </div>
          </div>
        )}

        {/* Year-by-Year Detail Table (Collapsible) */}
        <div className="mb-12">
          <button
            onClick={() => setShowDetail(!showDetail)}
            className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-blue-600 transition mb-4"
          >
            <span className="text-xl">{showDetail ? '▼' : '▶'}</span>
            {showDetail ? 'Hide year-by-year detail' : 'Show year-by-year detail'}
          </button>

          {showDetail && (() => {
            // Milestone detection
            const efCompleteAge = efCompleteYear > 0 ? data.clientAge + (efCompleteYear - currentYear) : 0;
            const mortgagePayoffAge = mortgagePayoffYear > 0 ? data.clientAge + Math.floor(payoffMonthsCalc / 12) : 0;
            const freedomAge = data.targetFreedomAge;

            function getMilestoneLabel(age: number): string | null {
              if (age === freedomAge) return '★ Freedom age';
              if (efCompleteAge > 0 && age === efCompleteAge) return '✓ Emergency fund complete';
              if (mortgagePayoffAge > 0 && age === mortgagePayoffAge) return '✓ Mortgage-free';
              return null;
            }

            return (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto max-h-[600px] overflow-y-auto relative">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900 bg-gray-50">Year</th>
                      <th className="px-6 py-3 text-left font-semibold text-gray-900 bg-gray-50">Age</th>
                      <th className="px-6 py-3 text-right font-semibold text-gray-900 bg-gray-50">Property Equity</th>
                      <th className="px-6 py-3 text-right font-semibold text-gray-900 bg-gray-50">Pension</th>
                      <th className="px-6 py-3 text-right font-semibold text-gray-900 bg-gray-50">ISA</th>
                      <th className="px-6 py-3 text-right font-semibold text-gray-900 bg-gray-50">Cash</th>
                      <th className="px-6 py-3 text-right font-semibold text-gray-900 bg-gray-50">Mortgage Bal.</th>
                      <th className="px-6 py-3 text-right font-semibold text-gray-900 bg-gray-50">Net Worth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((row, idx) => {
                      const milestone = getMilestoneLabel(row.age);
                      const isMilestone = milestone !== null;
                      const rowBg = isMilestone ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-[#f9fafb]';
                      const fontWeight = isMilestone ? 'font-bold' : '';
                      const borderLeft = isMilestone ? 'border-l-[3px] border-l-ma-navy' : '';

                      return (
                        <tr key={idx} className={`${rowBg} ${borderLeft}`}>
                          <td className={`px-6 py-3 text-gray-900 ${fontWeight}`}>
                            {row.year}
                            {milestone && <span className="ml-2 text-xs text-ma-navy">{milestone}</span>}
                          </td>
                          <td className={`px-6 py-3 text-gray-700 ${fontWeight}`}>{row.age}</td>
                          <td className={`px-6 py-3 text-right text-orange-600 ${isMilestone ? 'font-bold' : 'font-medium'}`}>
                            {formatCurrency(row.propertyEquity)}
                          </td>
                          <td className={`px-6 py-3 text-right text-purple-600 ${isMilestone ? 'font-bold' : 'font-medium'}`}>
                            {formatCurrency(row.pension)}
                          </td>
                          <td className={`px-6 py-3 text-right text-blue-600 ${isMilestone ? 'font-bold' : 'font-medium'}`}>
                            {formatCurrency(row.isa)}
                          </td>
                          <td className={`px-6 py-3 text-right text-teal-600 ${isMilestone ? 'font-bold' : 'font-medium'}`}>
                            {formatCurrency(row.cash)}
                          </td>
                          <td className={`px-6 py-3 text-right ${fontWeight}`}>
                            {row.mortgageBalance > 0 ? (
                              <span className="text-gray-700">{formatCurrency(row.mortgageBalance)}</span>
                            ) : (
                              <span className="text-green-600 font-semibold">✓ Paid</span>
                            )}
                          </td>
                          <td className={`px-6 py-3 text-right text-gray-900 ${isMilestone ? 'font-bold' : 'font-semibold'}`}>
                            {formatCurrency(row.netWorth)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>

        {/* Plan Narrative */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-bold mb-4">Your plan in plain English</h2>
          <p className="text-lg leading-relaxed text-gray-100">{planNarrative}</p>
        </div>

        {/* Plan Summary Stats */}
        <div className="bg-blue-50 rounded-lg p-8 border border-blue-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Plan summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Years to freedom</p>
              <p className="text-2xl font-bold text-blue-900">
                {data.targetFreedomAge - data.clientAge}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Mortgage status</p>
              <p className="text-2xl font-bold text-blue-900">
                {data.mortgageBalance && data.mortgageBalance > 0 ? 'Active' : 'Clear'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Monthly committed</p>
              <p className="text-2xl font-bold text-blue-900">
                {formatCurrency(data.planFinalMonthlyCommitted || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanPhasedPlan;
